import React, { useImperativeHandle, forwardRef } from "react"

import ResizableElementWrapper from "../shared/ResizableElementWrapper.component"
import PieChartVariableSelector from "./PieChartVariableSelector.component"
import PieChartDisplayArea from "./PieChartDisplayArea.component"
import { usePieChartContextMenu } from "./PieChartContextMenu.component"
import { usePieChartElement } from "features/Reporting/hooks/Elements/usePieChartElement"

import {
  DEFAULT_PIE_CHART_WIDTH,
  DEFAULT_PIE_CHART_HEIGHT,
} from "constants/dashboardElementSizes"

import {
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { ElementRef } from "../../ReportingDisplay.component"

/**
 * Element update payload for pie chart elements
 */
interface PieChartElementUpdate {
  width?: number
  height?: number
}

/**
 * Props for PieChartElement component
 */
export interface PieChartElementProps {
  /** Global filter string passed to data elements */
  filter?: string
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: PieChartElementUpdate) => void
  /** Configuration for pie chart element */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Whether in dashboard mode */
  isDashboard?: boolean
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

/**
 * Pie Chart Element Component (Phase 2 Refactored with Business Logic Hook)
 *
 * A refactored pie chart component using:
 * - usePieChartElement: Custom hook containing all business logic
 * - PieChartVariableSelector: Handles variable selection UI
 * - PieChartDisplayArea: Handles chart rendering and display states
 * - PieChartContextMenu: Handles context menu and submenus
 *
 * This demonstrates Phase 2 decomposition approach with extracted business logic.
 */
const PieChartElement = forwardRef<ElementRef, PieChartElementProps>(
  (props, ref) => {
    // Use the custom hook to get all business logic and state
    const pieChartLogic = usePieChartElement(props)

    // Destructure what we need from the hook
    const {
      // State
      chartData,
      convertedChartData,
      chartContent,
      showSelects,
      isLoading,
      isQueuedForExecution,
      error,
      hasAttemptedLoad,

      // Config values
      selectedVariable,
      selectedStatisticType,
      legendPosition,
      hideArcLabels,
      selectedDashboardCategories,

      // Hooks
      configHook,

      // Handlers
      handleRefresh,
      handleEdit,
      handleDelete,
      handleVariableSelect,
      handleStatisticTypeChange,
      handleToggleArcLabels,
      handleLegendPositionChange,
      handleCategoryToggle,
      requestSequentialExecution,

      // Imperative handle values
      hasDataAndShouldRefresh,

      // Refs and utilities
      chartContainerRef,
      selectOptions,
    } = pieChartLogic

    // Expose methods via useImperativeHandle
    useImperativeHandle(
      ref,
      () => ({
        hasDataAndShouldRefresh,
        requestSequentialExecution,
      }),
      [hasDataAndShouldRefresh, requestSequentialExecution]
    )

    // Generate handler with config management
    const handleGenerate = () => {
      configHook.setSkipNextConfigUpdate(false) // Don't skip since we want to save after run
      requestSequentialExecution("user_run")
    }

    // Context menu
    const { handleContextMenu, ContextMenus } = usePieChartContextMenu({
      hasData: !!chartData,
      showSelects,
      isDashboard: props.isDashboard || true,
      isLoading,
      isQueuedForExecution,
      legendPosition,
      convertedChartData,
      selectedStatisticType,
      selectedDashboardCategories,
      onDeleteRequest: props.onDeleteRequest,
      onEdit: handleEdit,
      onRefresh: handleRefresh,
      onDelete: handleDelete,
      onToggleArcLabels: handleToggleArcLabels,
      onLegendPositionChange: handleLegendPositionChange,
      onCategoryToggle: handleCategoryToggle,
    })

    // Styles
    const topBottomPaddingRem = 0.5

    const wrapperAdditionalClasses = `relative bg-white bg-opacity-90 rounded-md shadow-sm hover:shadow-md transition-shadow ${
      showSelects ? "inline-block" : ""
    }`

    const wrapperStyleOverrides: React.CSSProperties = {
      minWidth: "250px",
      minHeight: showSelects ? "auto" : "250px",
      overflow: "hidden",
      borderRadius: "4px",
      boxShadow:
        chartData && !showSelects ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
      ...(props.element.y >= 810
        ? {
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            margin: "0 auto",
          }
        : {}),
      ...(showSelects ? { width: "300px", height: "auto" } : {}),
    }

    const contentContainerStyle: React.CSSProperties = {
      width: "100%",
      height: "100%",
      padding: showSelects ? "0" : `${topBottomPaddingRem}rem`,
      display: "flex",
      flexDirection: "column",
      position: "relative",
    }

    const pieChartHandlesConfig = {
      horizontal: true,
      vertical: true,
      corner: true,
    }

    const chartAreaHeight = showSelects
      ? "auto"
      : `calc(100% - ${topBottomPaddingRem * 2 * 16}px)`

    return (
      <ResizableElementWrapper
        element={props.element}
        onUpdate={props.onUpdate}
        readonly={showSelects}
        minDimensions={{ width: 300, height: 250 }}
        defaultWidth={DEFAULT_PIE_CHART_WIDTH}
        defaultHeight={DEFAULT_PIE_CHART_HEIGHT}
        setSkipUpdateFlag={() => configHook.setSkipNextConfigUpdate(true)}
        additionalClasses={wrapperAdditionalClasses}
        styleOverrides={wrapperStyleOverrides}
        handleStyleType="visible"
        handlesConfiguration={pieChartHandlesConfig}
      >
        <div
          data-element-id={props.element.id}
          style={contentContainerStyle}
          onContextMenu={handleContextMenu}
        >
          {showSelects && (
            <PieChartVariableSelector
              selectOptions={selectOptions}
              selectedVariable={selectedVariable}
              selectedStatisticType={selectedStatisticType}
              queryInstance={!!props.queryInstance}
              isLoading={isLoading}
              isQueuedForExecution={isQueuedForExecution}
              onVariableSelect={handleVariableSelect}
              onStatisticTypeChange={handleStatisticTypeChange}
              onGenerate={handleGenerate}
            />
          )}

          {!showSelects && (
            <PieChartDisplayArea
              chartContent={chartContent}
              chartContainerRef={chartContainerRef}
              selectedStatisticType={selectedStatisticType}
              legendPosition={legendPosition}
              hideArcLabels={hideArcLabels}
              chartAreaHeight={chartAreaHeight}
              isLoading={isLoading}
              isQueuedForExecution={isQueuedForExecution}
              error={error}
              isInitializing={configHook.isInitializing}
              hasAttemptedLoad={hasAttemptedLoad}
              hasChartData={!!chartData}
              onRefresh={handleRefresh}
            />
          )}
        </div>

        {/* Context menu and submenus */}
        {ContextMenus}
      </ResizableElementWrapper>
    )
  }
)

PieChartElement.displayName = "PieChartElement"

export default PieChartElement
